/**
 * ContractDiffPanel.jsx
 * ======================
 * Contract comparison with AI semantic analysis.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  GitCompare, Loader2, AlertTriangle, CheckCircle2,
  FileText, Languages
} from 'lucide-react';
import { useCreditGate } from '../CreditGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractDiffPanel = ({ documents = [] }) => {
  const { requestRun, dialog } = useCreditGate('contract_diff', 'Confirm Contract Comparison');
  const [docA, setDocA] = useState('');
  const [docB, setDocB] = useState('');
  const [mode, setMode] = useState('semantic');
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localDocs, setLocalDocs] = useState([]);

  // Use documents prop or fetch if empty
  useEffect(() => {
    if (documents && documents.length > 0) {
      setLocalDocs(documents);
    } else {
      fetchDocuments();
    }
  }, [documents]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/documents/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const docs = Array.isArray(data) ? data : (data.documents || []);
        setLocalDocs(docs);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setLocalDocs([]);
    } finally {
      setLoading(false);
    }
  };

  // Use localDocs for display
  const displayDocs = localDocs.length > 0 ? localDocs : documents;

  const compareContracts = async () => {
    if (!docA || !docB) {
      toast.error('Please select two documents to compare');
      return;
    }

    setComparing(true);
    setResult(null);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/contract-diff/compare`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doc_id_a: docA,
          doc_id_b: docB,
          comparison_mode: mode
        })
      });

      if (!response.ok) throw new Error('Comparison failed');
      
      const data = await response.json();
      if (data.success === false) {
        toast.error(data.error || 'Comparison failed - could not read document content');
      } else {
        setResult(data);
        toast.success('Comparison complete!');
      }
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Failed to compare documents. Please try again.');
    } finally {
      setComparing(false);
    }
  };

  const getRiskBadge = (level) => {
    const colors = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    return colors[level] || colors.low;
  };

  return (
    <Card data-testid="contract-diff-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <GitCompare className="h-5 w-5 text-primary" />
          <CardTitle>Contract Comparison</CardTitle>
        </div>
        <CardDescription>
          Compare contracts with AI-powered semantic analysis
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && displayDocs.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No documents available for comparison</p>
            <p className="text-sm">Upload documents to use this feature</p>
          </div>
        )}

        {/* Document Selection */}
        {displayDocs.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Document A</label>
                <Select value={docA} onValueChange={setDocA}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select first document" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayDocs.map(doc => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Document B</label>
                <Select value={docB} onValueChange={setDocB}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select second document" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayDocs.map(doc => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

        {/* Comparison Mode */}
        <div>
          <label className="text-sm font-medium mb-2 block">Comparison Mode</label>
          <div className="flex gap-2">
            {[
              { value: 'semantic', label: 'Semantic', desc: 'AI meaning analysis' },
              { value: 'clause', label: 'Clause', desc: 'Clause-by-clause' },
              { value: 'full', label: 'Full', desc: 'Complete diff' }
            ].map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                className={`flex-1 p-3 rounded-lg border transition-all ${
                  mode === value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-muted-foreground">{desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Compare Button */}
        <Button 
          onClick={() => requestRun(compareContracts)} 
          disabled={comparing || !docA || !docB}
          className="w-full"
        >
          {comparing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Comparing...
            </>
          ) : (
            <>
              <GitCompare className="h-4 w-4 mr-2" />
              Compare Documents
            </>
          )}
        </Button>
          </>
        )}

        {/* Results */}
        {result && result.success && (
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Comparison Results</span>
              <Badge variant="outline">{result.comparison_mode}</Badge>
            </div>

            {/* Similarity Score */}
            {result.result?.similarity_score !== undefined && (
              <div className="flex items-center gap-4">
                <div className="text-3xl font-bold">
                  {Math.round(result.result.similarity_score)}%
                </div>
                <div className="text-sm text-muted-foreground">
                  Document Similarity
                </div>
              </div>
            )}

            {/* Risk Assessment */}
            {result.result?.risk_assessment && (
              <div className="space-y-2">
                <span className="text-sm font-medium">Risk Changes</span>
                <div className="flex gap-2 flex-wrap">
                  {result.result.risk_assessment.high?.length > 0 && (
                    <Badge className={getRiskBadge('high')}>
                      {result.result.risk_assessment.high.length} High Risk
                    </Badge>
                  )}
                  {result.result.risk_assessment.medium?.length > 0 && (
                    <Badge className={getRiskBadge('medium')}>
                      {result.result.risk_assessment.medium.length} Medium Risk
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Clause Differences (for clause mode) */}
            {result.result?.clause_differences && result.result.clause_differences.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Clause-by-Clause Comparison</span>
                  <span className="text-xs text-muted-foreground">
                    Doc A: {result.result.total_clauses_a} clauses | Doc B: {result.result.total_clauses_b} clauses
                  </span>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {result.result.clause_differences.map((diff, idx) => (
                    <div key={idx} className={`p-3 border rounded-lg text-sm ${
                      diff.status === 'modified' ? 'border-yellow-200 bg-yellow-50' :
                      diff.status === 'only_in_a' ? 'border-red-200 bg-red-50' :
                      diff.status === 'only_in_b' ? 'border-green-200 bg-green-50' :
                      diff.status === 'identical' ? 'border-gray-200 bg-gray-50' :
                      'border-blue-200 bg-blue-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{diff.clause_type?.replace(/_/g, ' ') || `Clause ${idx + 1}`}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            diff.status === 'modified' ? 'text-yellow-700 border-yellow-300' :
                            diff.status === 'only_in_a' ? 'text-red-700 border-red-300' :
                            diff.status === 'only_in_b' ? 'text-green-700 border-green-300' :
                            'text-gray-700 border-gray-300'
                          }`}>
                            {diff.status === 'only_in_a' ? 'Only in Doc A' :
                             diff.status === 'only_in_b' ? 'Only in Doc B' :
                             diff.status === 'modified' ? 'Modified' :
                             diff.status === 'identical' ? 'Identical' : diff.status}
                          </Badge>
                          {diff.similarity > 0 && (
                            <span className="text-xs text-muted-foreground">{Math.round(diff.similarity * 100)}% similar</span>
                          )}
                        </div>
                      </div>
                      {diff.doc_a && (
                        <div className="mb-2">
                          <div className="text-xs text-muted-foreground mb-1">Document A:</div>
                          <div className="text-xs bg-white/60 p-2 rounded max-h-20 overflow-y-auto">{diff.doc_a.substring(0, 300)}{diff.doc_a.length > 300 ? '...' : ''}</div>
                        </div>
                      )}
                      {diff.doc_b && (
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Document B:</div>
                          <div className="text-xs bg-white/60 p-2 rounded max-h-20 overflow-y-auto">{diff.doc_b.substring(0, 300)}{diff.doc_b.length > 300 ? '...' : ''}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full Diff Preview (unified diff format) */}
            {result.result?.diff_preview && result.result.diff_preview.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Full Document Diff</span>
                  {result.result.statistics && (
                    <div className="flex gap-2 text-xs">
                      <span className="text-green-600">+{result.result.statistics.lines_added} added</span>
                      <span className="text-red-600">-{result.result.statistics.lines_removed} removed</span>
                      <span className="text-muted-foreground">{result.result.statistics.total_changes} total changes</span>
                    </div>
                  )}
                </div>
                <div className="bg-slate-900 rounded-lg p-3 max-h-80 overflow-y-auto font-mono text-xs">
                  {result.result.diff_preview.slice(0, 100).map((line, idx) => {
                    const isAdd = line.startsWith('+') && !line.startsWith('+++');
                    const isDel = line.startsWith('-') && !line.startsWith('---');
                    const isHeader = line.startsWith('@@');
                    return (
                      <div key={idx} className={`px-2 ${
                        isAdd ? 'bg-green-900/40 text-green-300' :
                        isDel ? 'bg-red-900/40 text-red-300' :
                        isHeader ? 'text-cyan-400' :
                        'text-slate-300'
                      }`}>
                        {line}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full Diff (for full mode - line-by-line) */}
            {result.result?.differences && result.result.differences.length > 0 && (
              <div className="space-y-3">
                <span className="text-sm font-medium">Full Document Differences</span>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {result.result.differences.map((diff, idx) => (
                    <div key={idx} className={`p-2 border rounded text-xs font-mono ${
                      diff.type === 'addition' ? 'border-green-200 bg-green-50 text-green-800' :
                      diff.type === 'deletion' ? 'border-red-200 bg-red-50 text-red-800' :
                      'border-gray-200 bg-gray-50 text-gray-800'
                    }`}>
                      <span className="font-bold mr-2">
                        {diff.type === 'addition' ? '+' : diff.type === 'deletion' ? '-' : ' '}
                      </span>
                      {diff.content || diff.text || JSON.stringify(diff)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Analysis */}
            {(result.result?.ai_analysis || result.result?.semantic_analysis) && (
              <div className="space-y-2">
                <span className="text-sm font-medium">AI Analysis</span>
                <div className="bg-muted/30 rounded p-3 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {result.result.ai_analysis || result.result.semantic_analysis}
                </div>
              </div>
            )}

            {/* Summary for cases where main content is the overview */}
            {result.result?.summary && (
              <div className="bg-blue-50 rounded p-3 text-sm border border-blue-200">
                <span className="font-medium text-blue-800">Summary: </span>
                {typeof result.result.summary === 'string' ? (
                  <span className="text-blue-700">{result.result.summary}</span>
                ) : (
                  <div className="flex gap-3 flex-wrap mt-1">
                    {result.result.summary.identical > 0 && (
                      <Badge variant="outline" className="text-gray-700 border-gray-300">
                        {result.result.summary.identical} Identical
                      </Badge>
                    )}
                    {result.result.summary.modified > 0 && (
                      <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                        {result.result.summary.modified} Modified
                      </Badge>
                    )}
                    {result.result.summary.significantly_different > 0 && (
                      <Badge variant="outline" className="text-orange-700 border-orange-300">
                        {result.result.summary.significantly_different} Significantly Different
                      </Badge>
                    )}
                    {result.result.summary.only_in_a > 0 && (
                      <Badge variant="outline" className="text-red-700 border-red-300">
                        {result.result.summary.only_in_a} Only in Doc A
                      </Badge>
                    )}
                    {result.result.summary.only_in_b > 0 && (
                      <Badge variant="outline" className="text-green-700 border-green-300">
                        {result.result.summary.only_in_b} Only in Doc B
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
      {dialog}
    </Card>
  );
};

export default ContractDiffPanel;
